// SETTINGS V2 — MAP-A-FE2 map canvas (§5). The INNER half of the map section:
// owns the single useJsApiLoader call + the GoogleMap + pins. Split out of
// LocationsMapV2 so each file stays < 300 lines (§5.1). Mounted by the outer
// component ONLY when a key exists AND ≥1 pin is located AND the section is
// expanded → phone/empty users never load the Maps script.
//
// v2 stack: inline styles + scoped CSS-var tokens + shared Loader. NO
// MUI/dt/Tailwind authored. SEC-REQ-2/3: no import-time side effects, no
// hardcoded ids/keys (key read at render). No clustering — fine at ~40-location
// scale; revisit (port clusterLocations from serviceBoard/MapContainer) past
// ~200/tenant.
import React, { useCallback, useEffect, useRef } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

import useTranslation from "../../../../shared/hooks/useTranslation";
import Loader from "../../../../shared/components/Loader";
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
} from "../../../../shared/config/googleMapsConfig";
import type { Location } from "../../../machinery/components/locations/locationTypes";
import LocationsMapPinV2 from "./LocationsMapPinV2";

// Same private-getter pattern as AddressAutocomplete / serviceBoard MapContainer.
export const getGoogleMapsApiKey = (): string =>
  (window as any).app?.config?.googleMapsApiKey || "";

// POI-clean style + options — copied VERBATIM from serviceBoard MapContainer.tsx
// lines 35-60 (§0.8: COPY, do NOT import cross-module — private consts there).
const mapStyles = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];
const mapOptions = {
  styles: mapStyles,
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: "greedy" as const,
};

// Initial view (Central Europe) — fitBounds overrides it on load; only avoids a
// flash of ocean before the first frame.
const DEFAULT_CENTER = { lat: 49.8, lng: 15.5 };
const DEFAULT_ZOOM = 6;
const FOCUS_ZOOM = 14; // modest zoom-in on row-pin focus (map default fits ~country)

// Dashed placeholder — mirrors the page's dashed empty-state pattern. Shared with
// the outer component (no-key / zero-located states).
export const MapPlaceholder: React.FC<{ height: number; title?: string; text: string }> = ({
  height,
  title,
  text,
}) => (
  <div
    style={{
      height,
      border: "1px dashed var(--border)",
      borderRadius: 10,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      textAlign: "center",
      padding: "0 20px",
    }}
  >
    {title && (
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>{title}</div>
    )}
    <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{text}</div>
  </div>
);

export interface LocationsMapCanvasV2Props {
  pins: Location[]; // pre-filtered: latitude/longitude non-null
  onPinClick: (loc: Location) => void;
  height: number;
  /** Selected/focused location id → its pin renders red (§enhancement). */
  selectedId?: string | null;
  /** Bumped on each ROW-pin click → pan/zoom to `selectedId`'s pin. */
  focusNonce?: number;
  /** Map background click (not a pin) → deselect. */
  onBackgroundClick?: () => void;
}

const LocationsMapCanvasV2: React.FC<LocationsMapCanvasV2Props> = ({
  pins,
  onPinClick,
  height,
  selectedId,
  focusNonce,
  onBackgroundClick,
}) => {
  const { translate: t } = useTranslation("settings");
  const apiKey = getGoogleMapsApiKey();
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  // Focus deferred to onLoad when a row-pin click expanded a not-yet-mounted map.
  const pendingFocusRef = useRef<string | null>(null);

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || pins.length === 0) return;
    if (pins.length === 1) {
      map.setCenter({ lat: pins[0].latitude as number, lng: pins[0].longitude as number });
      map.setZoom(13);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    pins.forEach((p) =>
      bounds.extend({ lat: p.latitude as number, lng: p.longitude as number })
    );
    map.fitBounds(bounds, 40);
  }, [pins]);

  // Smooth pan + modest zoom-in to a pin (row-pin focus). Returns false if the map
  // isn't ready yet (caller stashes it in pendingFocusRef for onLoad).
  const applyFocus = useCallback(
    (id: string | null | undefined): boolean => {
      const map = mapRef.current;
      if (!map || !id) return false;
      const pin = pins.find((p) => p.id === id);
      if (!pin) return false;
      map.panTo({ lat: pin.latitude as number, lng: pin.longitude as number });
      const z = map.getZoom() ?? 0;
      if (z < FOCUS_ZOOM) map.setZoom(FOCUS_ZOOM);
      return true;
    },
    [pins]
  );

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      // If a focus is pending (map just expanded from collapsed), honor it; else fit.
      if (pendingFocusRef.current && applyFocus(pendingFocusRef.current)) {
        pendingFocusRef.current = null;
      } else {
        fit();
      }
    },
    [fit, applyFocus]
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Refit whenever the located-pin set changes (a create/edit adds a pin via the
  // page's onWriteSuccess refetch — §5.8).
  useEffect(() => {
    fit();
  }, [fit]);

  // Row-pin focus: on every focusNonce bump, pan+zoom to the selected pin. If the
  // map isn't mounted yet (was collapsed), stash for onLoad.
  useEffect(() => {
    if (!focusNonce) return;
    if (!applyFocus(selectedId)) pendingFocusRef.current = selectedId ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  const frame: React.CSSProperties = {
    width: "100%",
    height,
    borderRadius: 10,
    border: "1px solid var(--border)",
    overflow: "hidden",
  };

  if (loadError) {
    return <MapPlaceholder height={height} text={t("settings:locations.map.loadError")} />;
  }
  if (!isLoaded) {
    return (
      <div style={{ ...frame, display: "grid", placeItems: "center", background: "var(--bg-soft)" }}>
        <Loader />
      </div>
    );
  }

  return (
    <div style={frame}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        options={mapOptions}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={() => onBackgroundClick?.()}
      >
        {pins.map((p) => (
          <LocationsMapPinV2
            key={p.id}
            location={p}
            onClick={onPinClick}
            selected={p.id === selectedId}
          />
        ))}
      </GoogleMap>
    </div>
  );
};

export default LocationsMapCanvasV2;
